// src/components/workspace/costing/IndirectRatesPage.tsx
// Dedicated page for configuring company indirect rates (Fringe, Overhead, G&A, Profit)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, ArrowLeft, Save, Loader2, Info, Sparkles,
  AlertCircle, CheckCircle, TrendingUp, TrendingDown, Percent,
  Building2, Users, Briefcase, DollarSign, HelpCircle
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

interface IndirectRates {
  id?: string;
  fringe_rate: number;
  overhead_rate: number;
  ga_rate: number;
  profit_rate: number;
  material_handling_rate: number;
  subcontractor_handling_rate: number;
  is_dcaa_compliant: boolean;
  fiscal_year_end: string;
  rate_type: 'provisional' | 'final' | 'forward_pricing' | 'billing';
  effective_date: string;
  expiration_date: string | null;
  notes: string;
  // Default work-performance locality for labor-rate derivation (SCA wage
  // determinations are county-scoped; locality shifts market wages). Used when
  // the solicitation doesn't dictate an on-site place of performance.
  work_city: string | null;
  work_state: string | null;
  work_county: string | null;
}

interface ContractTypeTemplate {
  contract_type: string;
  display_name: string;
  typical_indirect_rates: {
    fringe: number;
    overhead: number;
    gAndA: number;
    profit: number;
  };
}

// Industry benchmark rates by company size and type
const INDUSTRY_BENCHMARKS = {
  fringe: {
    label: 'Fringe Benefits',
    description: 'Employee benefits, taxes, insurance, PTO, 401(k) match',
    ranges: {
      small: { min: 28, max: 38, typical: 32 },
      medium: { min: 30, max: 42, typical: 35 },
      large: { min: 32, max: 45, typical: 38 }
    },
    components: [
      { name: 'FICA/Medicare', typical: 7.65 },
      { name: 'FUTA/SUTA', typical: 1.5 },
      { name: 'Health Insurance', typical: 8.0 },
      { name: 'Dental/Vision', typical: 1.5 },
      { name: 'Life/Disability', typical: 1.0 },
      { name: '401(k) Match', typical: 4.0 },
      { name: 'PTO/Holiday', typical: 8.0 },
      { name: 'Workers Comp', typical: 1.5 }
    ]
  },
  overhead: {
    label: 'Overhead',
    description: 'Facilities, equipment, indirect labor, IT infrastructure',
    ranges: {
      small: { min: 25, max: 60, typical: 40 },
      medium: { min: 35, max: 80, typical: 55 },
      large: { min: 45, max: 120, typical: 75 }
    },
    components: [
      { name: 'Facilities/Rent', typical: 15.0 },
      { name: 'Utilities', typical: 3.0 },
      { name: 'IT Infrastructure', typical: 8.0 },
      { name: 'Indirect Labor', typical: 12.0 },
      { name: 'Depreciation', typical: 5.0 },
      { name: 'Insurance (General)', typical: 3.0 },
      { name: 'Supplies/Materials', typical: 2.0 }
    ]
  },
  gAndA: {
    label: 'G&A (General & Administrative)',
    description: 'Executive, finance, HR, legal, business development',
    ranges: {
      small: { min: 8, max: 18, typical: 12 },
      medium: { min: 10, max: 20, typical: 14 },
      large: { min: 10, max: 25, typical: 16 }
    },
    components: [
      { name: 'Executive Management', typical: 4.0 },
      { name: 'Finance/Accounting', typical: 3.0 },
      { name: 'Human Resources', typical: 2.0 },
      { name: 'Legal/Compliance', typical: 1.5 },
      { name: 'Business Development', typical: 2.5 },
      { name: 'Corporate Insurance', typical: 1.0 }
    ]
  },
  profit: {
    label: 'Fee/Profit',
    description: 'Contractor profit margin (limited by contract type)',
    ranges: {
      ffp: { min: 8, max: 15, typical: 10 },
      cpff: { min: 6, max: 10, typical: 8 },
      tm: { min: 8, max: 12, typical: 10 },
      idiq: { min: 8, max: 15, typical: 10 }
    },
    note: 'FAR 15.404-4 limits profit based on contract type and risk'
  }
};

const DEFAULT_RATES: IndirectRates = {
  fringe_rate: 32,
  overhead_rate: 45,
  ga_rate: 12,
  profit_rate: 10,
  material_handling_rate: 0,
  subcontractor_handling_rate: 0,
  is_dcaa_compliant: false,
  fiscal_year_end: '12/31',
  rate_type: 'provisional',
  effective_date: new Date().toISOString().split('T')[0],
  expiration_date: null,
  notes: '',
  work_city: null,
  work_state: null,
  work_county: null
};

export function IndirectRatesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rates, setRates] = useState<IndirectRates>(DEFAULT_RATES);
  const [templates, setTemplates] = useState<ContractTypeTemplate[]>([]);
  const [companySize, setCompanySize] = useState<'small' | 'medium' | 'large'>('small');
  const [showBreakdown, setShowBreakdown] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (profile) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load existing indirect rates
      const { data: ratesData, error: ratesError } = await supabase
        .from('company_indirect_rates')
        .select('*')
        .eq('profile_id', profile?.id)
        .single();

      if (ratesError && ratesError.code !== 'PGRST116') {
        console.error('Error loading indirect rates:', ratesError);
      }

      // Load contract type templates for comparison
      const { data: templateData, error: templateError } = await supabase
        .from('contract_type_templates')
        .select('contract_type, display_name, typical_indirect_rates');

      if (templateError && templateError.code !== 'PGRST116') {
        console.error('Error loading templates:', templateError);
      }

      if (ratesData) {
        setRates(ratesData);
      }
      setTemplates(templateData || []);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load indirect rates');
    } finally {
      setLoading(false);
    }
  };

  const calculateWrapRate = (): number => {
    // Industry-standard compounded wrap rate calculation
    return (1 + rates.fringe_rate / 100) *
           (1 + rates.overhead_rate / 100) *
           (1 + rates.ga_rate / 100) *
           (1 + rates.profit_rate / 100);
  };

  const calculateAdditiveRate = (): number => {
    // Alternative additive method (less common)
    return 1 + (rates.fringe_rate + rates.overhead_rate + rates.ga_rate + rates.profit_rate) / 100;
  };

  const updateRate = (key: keyof IndirectRates, value: any) => {
    setRates(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const applyTemplate = (template: ContractTypeTemplate) => {
    setRates(prev => ({
      ...prev,
      fringe_rate: template.typical_indirect_rates.fringe,
      overhead_rate: template.typical_indirect_rates.overhead,
      ga_rate: template.typical_indirect_rates.gAndA,
      profit_rate: template.typical_indirect_rates.profit
    }));
    setHasUnsavedChanges(true);
    toast.success(`Applied ${template.display_name} rates`);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const rateData = {
        profile_id: profile?.id,
        fringe_rate: rates.fringe_rate,
        overhead_rate: rates.overhead_rate,
        ga_rate: rates.ga_rate,
        profit_rate: rates.profit_rate,
        material_handling_rate: rates.material_handling_rate,
        subcontractor_handling_rate: rates.subcontractor_handling_rate,
        is_dcaa_compliant: rates.is_dcaa_compliant,
        fiscal_year_end: rates.fiscal_year_end,
        rate_type: rates.rate_type,
        effective_date: rates.effective_date,
        expiration_date: rates.expiration_date,
        notes: rates.notes,
        work_city: rates.work_city?.trim() || null,
        work_state: rates.work_state?.trim() || null,
        work_county: rates.work_county?.trim() || null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('company_indirect_rates')
        .upsert(rateData, { onConflict: 'profile_id' });

      if (error) throw error;

      toast.success('Indirect rates saved successfully!');
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error('Error saving indirect rates:', error);
      toast.error('Failed to save indirect rates');
    } finally {
      setSaving(false);
    }
  };

  const getCompetitivenessIndicator = (wrapRate: number) => {
    if (wrapRate < 1.6) {
      return { status: 'low', color: 'yellow', message: 'Below typical - verify all costs are captured' };
    } else if (wrapRate <= 2.3) {
      return { status: 'competitive', color: 'green', message: 'Competitive range for federal contracts' };
    } else if (wrapRate <= 2.8) {
      return { status: 'high', color: 'yellow', message: 'Above average - may be less competitive' };
    } else {
      return { status: 'very_high', color: 'red', message: 'High wrap rate - review for competitiveness' };
    }
  };

  const wrapRate = calculateWrapRate();
  const competitiveness = getCompetitivenessIndicator(wrapRate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto p-6'}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {!embedded && (
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          )}
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Calculator className="h-6 w-6 mr-2 text-bw-primary" />
            Indirect Rates Configuration
          </h1>
          <p className="text-bw-muted mt-1">
            Configure your company's Fringe, Overhead, G&A, and Profit rates for wrap rate calculations
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Rates
        </button>
      </div>

      {/* Wrap Rate Summary Card */}
      <div className={`${
        competitiveness.color === 'green' ? 'bg-success-bg border-success-border' :
        competitiveness.color === 'yellow' ? 'bg-fit-moderate-bg border-fit-moderate-border' :
        'bg-urgent-bg border-urgent-border'
      } rounded-xl p-6 border mb-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-bw-ink">Your Wrap Rate</h2>
            <p className="text-sm text-bw-muted mt-1">
              $100/hr base = <span className="font-bold">${(100 * wrapRate).toFixed(2)}/hr</span> fully loaded
            </p>
            <div className="flex items-center mt-2">
              {competitiveness.color === 'green' ? (
                <CheckCircle className="h-4 w-4 text-success mr-1" />
              ) : competitiveness.color === 'yellow' ? (
                <AlertCircle className="h-4 w-4 text-fit-moderate mr-1" />
              ) : (
                <AlertCircle className="h-4 w-4 text-urgent mr-1" />
              )}
              <span className={`text-sm ${
                competitiveness.color === 'green' ? 'text-success' :
                competitiveness.color === 'yellow' ? 'text-fit-moderate' :
                'text-urgent'
              }`}>
                {competitiveness.message}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-bw-ink">{wrapRate.toFixed(3)}x</div>
            <div className="text-sm text-bw-muted">Compounded Method</div>
          </div>
        </div>

        {/* Visual Breakdown Bar */}
        <div className="mt-6">
          <div className="flex items-center h-10 rounded-lg overflow-hidden">
            <div
              className="h-full bg-bw-primary-tint0 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${100 / wrapRate}%` }}
            >
              Base
            </div>
            <div
              className="h-full bg-success-bg0 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${(rates.fringe_rate / 100) * (100 / wrapRate)}%` }}
            >
              +{rates.fringe_rate}%
            </div>
            <div
              className="h-full bg-fit-moderate-bg0 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${(rates.overhead_rate / 100) * (100 / wrapRate)}%` }}
            >
              +{rates.overhead_rate}%
            </div>
            <div
              className="h-full bg-orange-500 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${(rates.ga_rate / 100) * (100 / wrapRate)}%` }}
            >
              +{rates.ga_rate}%
            </div>
            <div
              className="h-full bg-bw-primary-tint0 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${(rates.profit_rate / 100) * (100 / wrapRate)}%` }}
            >
              +{rates.profit_rate}%
            </div>
          </div>
          <div className="flex justify-between text-xs text-bw-muted mt-2">
            <span>Base Rate</span>
            <span>Fringe</span>
            <span>Overhead</span>
            <span>G&A</span>
            <span>Profit</span>
          </div>
        </div>
      </div>

      {/* Industry Templates */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4 mb-6">
        <div className="flex items-center mb-3">
          <Sparkles className="h-5 w-5 text-bw-primary mr-2" />
          <h3 className="font-semibold text-bw-ink">Apply Industry-Standard Rates</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {templates.map((template) => (
            <button
              key={template.contract_type}
              onClick={() => applyTemplate(template)}
              className="px-3 py-1.5 bg-bw-surface-subtle border border-bw-border rounded-lg text-sm hover:bg-bw-primary-tint hover:border-purple-200 transition-colors"
            >
              <span className="font-medium">{template.display_name}</span>
              <span className="text-bw-muted ml-1">
                ({((1 + template.typical_indirect_rates.fringe / 100) *
                   (1 + template.typical_indirect_rates.overhead / 100) *
                   (1 + template.typical_indirect_rates.gAndA / 100) *
                   (1 + template.typical_indirect_rates.profit / 100)).toFixed(2)}x)
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Rate Configuration Cards */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Fringe Benefits */}
        <div className="bg-bw-surface rounded-xl border border-bw-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-2 bg-success-bg rounded-lg mr-3">
                <Users className="h-5 w-5 text-success" />
              </div>
              <div>
                <h3 className="font-semibold text-bw-ink">{INDUSTRY_BENCHMARKS.fringe.label}</h3>
                <p className="text-xs text-bw-muted">{INDUSTRY_BENCHMARKS.fringe.description}</p>
              </div>
            </div>
            <button
              onClick={() => setShowBreakdown(showBreakdown === 'fringe' ? null : 'fringe')}
              className="text-bw-muted hover:text-bw-muted"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center mb-3">
            <input
              type="number"
              value={rates.fringe_rate}
              onChange={(e) => updateRate('fringe_rate', parseFloat(e.target.value) || 0)}
              className="w-24 text-2xl font-bold text-bw-ink border-b-2 border-bw-border focus:border-bw-primary focus:outline-none text-center"
            />
            <Percent className="h-5 w-5 text-bw-muted ml-1" />
          </div>

          <input
            type="range"
            min={0}
            max={60}
            value={rates.fringe_rate}
            onChange={(e) => updateRate('fringe_rate', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mb-2"
          />

          <div className="flex justify-between text-xs text-bw-muted">
            <span>Industry: {INDUSTRY_BENCHMARKS.fringe.ranges[companySize].min}% - {INDUSTRY_BENCHMARKS.fringe.ranges[companySize].max}%</span>
            <span className={
              rates.fringe_rate >= INDUSTRY_BENCHMARKS.fringe.ranges[companySize].min &&
              rates.fringe_rate <= INDUSTRY_BENCHMARKS.fringe.ranges[companySize].max
                ? 'text-success' : 'text-fit-moderate'
            }>
              Typical: {INDUSTRY_BENCHMARKS.fringe.ranges[companySize].typical}%
            </span>
          </div>

          {showBreakdown === 'fringe' && (
            <div className="mt-4 p-3 bg-bw-surface-subtle rounded-lg text-xs">
              <p className="font-medium mb-2">Typical Components:</p>
              {INDUSTRY_BENCHMARKS.fringe.components.map((comp, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span>{comp.name}</span>
                  <span>{comp.typical}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Overhead */}
        <div className="bg-bw-surface rounded-xl border border-bw-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-2 bg-fit-moderate-bg rounded-lg mr-3">
                <Building2 className="h-5 w-5 text-fit-moderate" />
              </div>
              <div>
                <h3 className="font-semibold text-bw-ink">{INDUSTRY_BENCHMARKS.overhead.label}</h3>
                <p className="text-xs text-bw-muted">{INDUSTRY_BENCHMARKS.overhead.description}</p>
              </div>
            </div>
            <button
              onClick={() => setShowBreakdown(showBreakdown === 'overhead' ? null : 'overhead')}
              className="text-bw-muted hover:text-bw-muted"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center mb-3">
            <input
              type="number"
              value={rates.overhead_rate}
              onChange={(e) => updateRate('overhead_rate', parseFloat(e.target.value) || 0)}
              className="w-24 text-2xl font-bold text-bw-ink border-b-2 border-bw-border focus:border-bw-primary focus:outline-none text-center"
            />
            <Percent className="h-5 w-5 text-bw-muted ml-1" />
          </div>

          <input
            type="range"
            min={0}
            max={150}
            value={rates.overhead_rate}
            onChange={(e) => updateRate('overhead_rate', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mb-2"
          />

          <div className="flex justify-between text-xs text-bw-muted">
            <span>Industry: {INDUSTRY_BENCHMARKS.overhead.ranges[companySize].min}% - {INDUSTRY_BENCHMARKS.overhead.ranges[companySize].max}%</span>
            <span className={
              rates.overhead_rate >= INDUSTRY_BENCHMARKS.overhead.ranges[companySize].min &&
              rates.overhead_rate <= INDUSTRY_BENCHMARKS.overhead.ranges[companySize].max
                ? 'text-success' : 'text-fit-moderate'
            }>
              Typical: {INDUSTRY_BENCHMARKS.overhead.ranges[companySize].typical}%
            </span>
          </div>

          {showBreakdown === 'overhead' && (
            <div className="mt-4 p-3 bg-bw-surface-subtle rounded-lg text-xs">
              <p className="font-medium mb-2">Typical Components:</p>
              {INDUSTRY_BENCHMARKS.overhead.components.map((comp, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span>{comp.name}</span>
                  <span>{comp.typical}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* G&A */}
        <div className="bg-bw-surface rounded-xl border border-bw-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg mr-3">
                <Briefcase className="h-5 w-5 text-fit-moderate" />
              </div>
              <div>
                <h3 className="font-semibold text-bw-ink">{INDUSTRY_BENCHMARKS.gAndA.label}</h3>
                <p className="text-xs text-bw-muted">{INDUSTRY_BENCHMARKS.gAndA.description}</p>
              </div>
            </div>
            <button
              onClick={() => setShowBreakdown(showBreakdown === 'ga' ? null : 'ga')}
              className="text-bw-muted hover:text-bw-muted"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center mb-3">
            <input
              type="number"
              value={rates.ga_rate}
              onChange={(e) => updateRate('ga_rate', parseFloat(e.target.value) || 0)}
              className="w-24 text-2xl font-bold text-bw-ink border-b-2 border-bw-border focus:border-bw-primary focus:outline-none text-center"
            />
            <Percent className="h-5 w-5 text-bw-muted ml-1" />
          </div>

          <input
            type="range"
            min={0}
            max={35}
            value={rates.ga_rate}
            onChange={(e) => updateRate('ga_rate', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mb-2"
          />

          <div className="flex justify-between text-xs text-bw-muted">
            <span>Industry: {INDUSTRY_BENCHMARKS.gAndA.ranges[companySize].min}% - {INDUSTRY_BENCHMARKS.gAndA.ranges[companySize].max}%</span>
            <span className={
              rates.ga_rate >= INDUSTRY_BENCHMARKS.gAndA.ranges[companySize].min &&
              rates.ga_rate <= INDUSTRY_BENCHMARKS.gAndA.ranges[companySize].max
                ? 'text-success' : 'text-fit-moderate'
            }>
              Typical: {INDUSTRY_BENCHMARKS.gAndA.ranges[companySize].typical}%
            </span>
          </div>

          {showBreakdown === 'ga' && (
            <div className="mt-4 p-3 bg-bw-surface-subtle rounded-lg text-xs">
              <p className="font-medium mb-2">Typical Components:</p>
              {INDUSTRY_BENCHMARKS.gAndA.components.map((comp, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span>{comp.name}</span>
                  <span>{comp.typical}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profit/Fee */}
        <div className="bg-bw-surface rounded-xl border border-bw-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-2 bg-bw-primary-tint rounded-lg mr-3">
                <DollarSign className="h-5 w-5 text-bw-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-bw-ink">{INDUSTRY_BENCHMARKS.profit.label}</h3>
                <p className="text-xs text-bw-muted">{INDUSTRY_BENCHMARKS.profit.description}</p>
              </div>
            </div>
            <button
              onClick={() => setShowBreakdown(showBreakdown === 'profit' ? null : 'profit')}
              className="text-bw-muted hover:text-bw-muted"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center mb-3">
            <input
              type="number"
              value={rates.profit_rate}
              onChange={(e) => updateRate('profit_rate', parseFloat(e.target.value) || 0)}
              className="w-24 text-2xl font-bold text-bw-ink border-b-2 border-bw-border focus:border-bw-primary focus:outline-none text-center"
            />
            <Percent className="h-5 w-5 text-bw-muted ml-1" />
          </div>

          <input
            type="range"
            min={0}
            max={20}
            value={rates.profit_rate}
            onChange={(e) => updateRate('profit_rate', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mb-2"
          />

          <div className="flex justify-between text-xs text-bw-muted">
            <span>FAR Limits: 6% - 15%</span>
            <span className={rates.profit_rate <= 15 ? 'text-success' : 'text-urgent'}>
              FFP Typical: {INDUSTRY_BENCHMARKS.profit.ranges.ffp.typical}%
            </span>
          </div>

          {showBreakdown === 'profit' && (
            <div className="mt-4 p-3 bg-bw-surface-subtle rounded-lg text-xs">
              <p className="font-medium mb-2">FAR 15.404-4 Profit Guidelines:</p>
              <div className="flex justify-between py-0.5">
                <span>Firm Fixed Price (FFP)</span>
                <span>{INDUSTRY_BENCHMARKS.profit.ranges.ffp.min}%-{INDUSTRY_BENCHMARKS.profit.ranges.ffp.max}%</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span>Cost Plus Fixed Fee (CPFF)</span>
                <span>{INDUSTRY_BENCHMARKS.profit.ranges.cpff.min}%-{INDUSTRY_BENCHMARKS.profit.ranges.cpff.max}%</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span>Time & Materials (T&M)</span>
                <span>{INDUSTRY_BENCHMARKS.profit.ranges.tm.min}%-{INDUSTRY_BENCHMARKS.profit.ranges.tm.max}%</span>
              </div>
              <p className="mt-2 text-bw-muted italic">{INDUSTRY_BENCHMARKS.profit.note}</p>
            </div>
          )}
        </div>
      </div>

      {/* Additional Settings */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-5 mb-6">
        <h3 className="font-semibold text-bw-ink mb-4">Additional Settings</h3>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">Material Handling Rate</label>
            <div className="flex items-center">
              <input
                type="number"
                value={rates.material_handling_rate}
                onChange={(e) => updateRate('material_handling_rate', parseFloat(e.target.value) || 0)}
                className="w-24 px-3 py-2 border border-bw-border rounded-lg text-sm"
              />
              <span className="ml-2 text-bw-muted">%</span>
            </div>
            <p className="text-xs text-bw-muted mt-1">Applied to material costs</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">Subcontractor Handling Rate</label>
            <div className="flex items-center">
              <input
                type="number"
                value={rates.subcontractor_handling_rate}
                onChange={(e) => updateRate('subcontractor_handling_rate', parseFloat(e.target.value) || 0)}
                className="w-24 px-3 py-2 border border-bw-border rounded-lg text-sm"
              />
              <span className="ml-2 text-bw-muted">%</span>
            </div>
            <p className="text-xs text-bw-muted mt-1">Applied to subcontractor costs</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">Rate Type</label>
            <select
              value={rates.rate_type}
              onChange={(e) => updateRate('rate_type', e.target.value)}
              className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
            >
              <option value="provisional">Provisional</option>
              <option value="final">Final</option>
              <option value="forward_pricing">Forward Pricing</option>
              <option value="billing">Billing</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">Fiscal Year End</label>
            <input
              type="text"
              value={rates.fiscal_year_end}
              onChange={(e) => updateRate('fiscal_year_end', e.target.value)}
              placeholder="MM/DD"
              className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">Effective Date</label>
            <input
              type="date"
              value={rates.effective_date}
              onChange={(e) => updateRate('effective_date', e.target.value)}
              className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
            />
          </div>
          <div className="flex items-center pt-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={rates.is_dcaa_compliant}
                onChange={(e) => updateRate('is_dcaa_compliant', e.target.checked)}
                className="rounded border-bw-border text-bw-primary focus:ring-bw-primary"
              />
              <span className="ml-2 text-sm text-bw-ink">DCAA Compliant Rates</span>
            </label>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-bw-ink mb-1">Notes</label>
          <textarea
            value={rates.notes}
            onChange={(e) => updateRate('notes', e.target.value)}
            placeholder="Any notes about your rate structure..."
            rows={2}
            className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Work Locality */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-5 mb-6">
        <div className="flex items-center mb-1">
          <Building2 className="h-5 w-5 text-bw-primary mr-2" />
          <h3 className="font-semibold text-bw-ink">Work Locality</h3>
        </div>
        <p className="text-sm text-bw-muted mb-4">
          Where your team performs the work. This drives locality labor rates — SCA wage
          determinations are county-scoped, and market wages shift by metro area. Proposals use
          this when the solicitation doesn't dictate an on-site place of performance (e.g. work
          performed at your facility); a solicitation-specified location always takes precedence.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">City</label>
            <input
              type="text"
              value={rates.work_city ?? ''}
              onChange={(e) => updateRate('work_city', e.target.value)}
              placeholder="e.g. Charlotte"
              className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">State</label>
            <input
              type="text"
              value={rates.work_state ?? ''}
              onChange={(e) => updateRate('work_state', e.target.value)}
              placeholder="e.g. NC"
              maxLength={20}
              className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              County <span className="font-normal text-bw-muted">(for SCA wage determinations)</span>
            </label>
            <input
              type="text"
              value={rates.work_county ?? ''}
              onChange={(e) => updateRate('work_county', e.target.value)}
              placeholder="e.g. Mecklenburg"
              className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
            />
          </div>
        </div>
        {!rates.work_city && !rates.work_state && (
          <p className="text-xs text-bw-muted mt-3">
            Not set — proposals fall back to the solicitation's place of performance, or national-average
            wages when neither is available.
          </p>
        )}
      </div>

      {/* Calculation Explanation */}
      <div className="bg-bw-primary-tint rounded-xl border border-blue-200 p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-bw-primary mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-bw-primary">Wrap Rate Calculation Method</h4>
            <p className="text-sm text-bw-primary mt-1">
              <strong>Compounded Method (Industry Standard):</strong><br />
              Loaded Rate = Base Rate × (1 + Fringe%) × (1 + Overhead%) × (1 + G&A%) × (1 + Profit%)<br />
              = ${100} × {(1 + rates.fringe_rate/100).toFixed(3)} × {(1 + rates.overhead_rate/100).toFixed(3)} × {(1 + rates.ga_rate/100).toFixed(3)} × {(1 + rates.profit_rate/100).toFixed(3)}<br />
              = <strong>${(100 * wrapRate).toFixed(2)}/hr</strong>
            </p>
            <p className="text-xs text-bw-primary mt-2">
              This compounding method is the standard for federal government contracting and DCAA audits.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
